import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useStyles } from './CreationsPage.styles.js';

import moment from 'moment';
import _ from 'lodash';
import Grid from '@material-ui/core/Grid';
import ReactTimeAgo from 'react-time-ago';

import HtmlTooltip from '../HtmlTooltip';
import InfiniteScroll from 'react-infinite-scroll-component';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';

import DownloadIcon from '@mui/icons-material/FileDownload';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';

import coverImg from '../../img/cover.png';
import infoIcon from '../../img/info.svg';

import DownloadModal from '../layout/DownloadModal.js';
import ConfirmationModal from '../layout/ConfirmationModal.js';
import EditLabelModal from '../layout/EditLabelModal.js';
import TTMInfoModal from '../layout/TTMInfoModal.js';
import { showMessageV2 } from '../../utils/page.utils.js';
import { appActions } from '../../actions/app.actions.js';

import {
  checkInferenceCompletion,
  checkFailedInferencesAndReturnCredit,
  updateInferenceById,
  updateVocalExtractById,
  checkVocalExtractCompletion,
  fetchInferencesChunk,
  fetchVocalExtractsChunk,
  checkVideoRenderCompletion,
  updateVideoRenderById,
  fetchVideoRendersChunk,
  getVideoRenderSignedUrl,
  fetchTrainingsLabel,
  fetchFaceSwapsChunk,
  updateFaceSwapById,
  checkFaceSwapCompletion,
  fetchRenderedVideoUrl,
  fetchTTIInferenceChunk,
  checkTTIInferenceCompletion,
  updateTTIInferenceById,
  getTTIInferenceSignedUrl,
  fetchTTMInferenceChunk,
  checkTTMInferenceCompletion,
  updateTTMInferenceById,
  updateTTSInferenceById,
  fetchTTSInferencesChunk,
  getTTSInferenceBlob,
  getRecentGenerationService,
} from '../../services/page.services.js';

import getHistoryTabs from './historyTabs.js';

function CreationsPage() {
  const { t } = useTranslation();
  const user = useSelector(state => state.user);
  const userId = user?.id;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [alignment, setAlignment] = useState('music');
  const historyTabs = getHistoryTabs(t, 'creations');
  const [loading, setLoading] = useState(true);

  // initData
  const [data, setData] = useState([]);

  // query
  const [query, setQuery] = useState('');

  // pagination
  const chunk = 50;
  const [chunkPage, setChunkPage] = useState(0);
  const [length, setLength] = useState(0);
  const controllerRef = useRef(null);

  // modal
  const [editModalProps, setEditModalProps] = useState({});
  const [confirmationModalProps, setConfirmationModalProps] = useState({});
  const [downloadModalProps, setDownloadModalProps] = useState({});
  const [ttmInfoModalProps, setTTMInfoModalProps] = useState({});
  const [downloadingId, setDownloadingId] = useState();

  // filter
  const [voice, setVoice] = useState([]);
  const [voiceId, setVoiceId] = useState([]);
  const [renderFilter, setRenderFilter] = useState(false);

  const getRecentService = async () => {
    const service = await getRecentGenerationService();
    setAlignment(service);
    setLoading(false);
  };

  useEffect( () => {
    if (userId) {
      getRecentService();
    }
  }, [userId]);

  useEffect(() => {
    const debounced = _.debounce(() => initData(false), 300);

    if (userId) {
      debounced();
    }

    return () => {
      debounced.cancel();
    };
  }, [userId, alignment, query, voiceId]);

  const initVoice = async () => {
    try {
      const initialVoice = await fetchTrainingsLabel();
      const copiedVoice = initialVoice.map(item => ({
        label: item.voice.label,
        count: parseInt(item.count),
        trainingId: item.trainingId,
      }));
      setVoice(copiedVoice);
    } catch (err) {
      console.log(err);
      showMessageV2(dispatch, t('historyTab.modal.filterError'));
      setVoice([]);
    }
  };

  const initData = async scroll => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    controllerRef.current = new AbortController();
    const signal = controllerRef.current.signal;

    try {
      if (alignment === 'music') {
        let inferencesChunk;
        await checkFailedInferencesAndReturnCredit();

        inferencesChunk = await fetchInferencesChunk(chunk, scroll ? chunkPage : 0, query, signal, voiceId);
        let inferences = inferencesChunk.rows;

        const incompleteList = _.filter(inferences, { status: 'PENDING' });
        if (_.size(incompleteList) > 0) {
          await checkInferenceCompletion(userId, incompleteList);
          inferencesChunk = await fetchInferencesChunk(chunk, scroll ? chunkPage : 0, query, signal, voiceId);
          inferences = inferencesChunk.rows;
        }
        const cleanedData = inferences.map(
          ({
            pitch,
            mixerSettings,
            voiceId,
            id,
            label,
            status,
            createdAt,
            needMdx,
            outputFormat,
            tier,
            deletedAt,
            sourceLabel,
            purchaseId,
            voice,
            watermark,
            pipelineStatus,
          }) => {
            let decodedMixerSettings;
            try {
              const decodedString = atob(mixerSettings);
              decodedMixerSettings = JSON.parse(decodedString);
            } catch (error) {
              decodedMixerSettings = null;
            }
            return {
              pitch,
              mixerSettings: decodedMixerSettings,
              trainingId: voiceId,
              id,
              label,
              createdAt: moment(createdAt).format('MM/DD/YY hh:mm A'),
              status,
              needMdx,
              outputFormat,
              tag: tier === 'FREE' && (watermark || watermark === null) ? `[${t('historyTab.freePrefix')}]` : '',
              deletedAt,
              sourceLabel,
              trainingLabel: voice ? voice.label : 'AI voice not found',
              pipelineStatus,
            };
          }
        );

        if (scroll) {
          setData(prevData => [...prevData, ...cleanedData]);
          setChunkPage(prevChunkPage => prevChunkPage + 1);
        } else {
          setData(cleanedData);
          setChunkPage(1);
        }
        setLength(inferencesChunk.count);
      } else if (alignment === 'vocalExtract') {
        let vocalExtractsChunk;
        vocalExtractsChunk = await fetchVocalExtractsChunk(chunk, scroll ? chunkPage : 0, query, signal);
        let vocalExtracts = vocalExtractsChunk.rows;

        const incompleteList = _.filter(vocalExtracts, {
          status: 'PENDING',
        });
        if (_.size(incompleteList) > 0) {
          await checkVocalExtractCompletion(userId, incompleteList);
          vocalExtractsChunk = await fetchVocalExtractsChunk(chunk, scroll ? chunkPage : 0, query, signal);
          vocalExtracts = vocalExtractsChunk.rows;
        }
        const cleanedData = vocalExtracts.map(({ id, label, status, createdAt, outputFormat }) => {
          return {
            id,
            label,
            createdAt: moment(createdAt).format('MM/DD/YY hh:mm A'),
            status,
            outputFormat,
            tag: '',
          };
        });

        if (scroll) {
          setData(prevData => [...prevData, ...cleanedData]);
          setChunkPage(prevChunkPage => prevChunkPage + 1);
        } else {
          setData(cleanedData);
          setChunkPage(1);
        }
        setLength(vocalExtractsChunk.count);
      } else if (alignment === 'speech') {
        let inferencesChunk;
        if (scroll) {
          inferencesChunk = await fetchTTSInferencesChunk(chunk, chunkPage, query, signal);
        } else {
          inferencesChunk = await fetchTTSInferencesChunk(chunk, 0, query, signal);
        }
        let inferences = inferencesChunk.rows;
        const cleanedData = inferences.map(
          ({
            ttsVoiceId,
            id,
            label,
            status,
            createdAt,
            needMdx,
            outputFormat,
            tier,
            deletedAt,
            sourceLabel,
            purchaseId,
            ttsVoice,
            variability,
            similarity,
          }) => {
            return {
              ttsTrainingId: ttsVoiceId,
              id,
              label,
              createdAt: moment(createdAt).format('MM/DD/YY hh:mm A'),
              status,
              needMdx,
              outputFormat,
              tag: '',
              deletedAt,
              sourceLabel,
              trainingLabel: ttsVoice ? ttsVoice.label : 'AI voice not found',
              variability,
              similarity,
            };
          }
        );

        if (scroll) {
          setData([...data, ...cleanedData]);
          setChunkPage(prevChunkPage => prevChunkPage + 1);
        } else {
          setData(cleanedData);
          setChunkPage(1);
        }
        setLength(inferencesChunk.count);
      } else if (alignment === 'faceSwap') {
        let faceSwapsChunk = await fetchFaceSwapsChunk(chunk, scroll ? chunkPage : 0, query, signal);
        let faceSwaps = faceSwapsChunk.rows;

        const incompleteList = _.filter(faceSwaps, { status: 'PENDING' });
        if (_.size(incompleteList) > 0) {
          await checkFaceSwapCompletion(userId, incompleteList);
          faceSwapsChunk = await fetchFaceSwapsChunk(chunk, scroll ? chunkPage : 0, query, signal);
          faceSwaps = faceSwapsChunk.rows;
        }

        const cleanedData = faceSwaps.map(({ id, label, status, createdAt, watermark, deletedAt }) => ({
          id,
          label,
          createdAt: moment(createdAt).format('MM/DD/YY hh:mm A'),
          status,
          deletedAt,
          watermark,
        }));

        if (scroll) {
          setData(prevData => [...prevData, ...cleanedData]);
          setChunkPage(prevChunkPage => prevChunkPage + 1);
        } else {
          setData(cleanedData);
          setChunkPage(1);
        }
        setLength(faceSwapsChunk.count);
      } else if (alignment === 'image') {
        let imageChunk;
        imageChunk = await fetchTTIInferenceChunk(chunk, scroll ? chunkPage : 0, query, signal);
        let images = imageChunk.rows;
        const incompleteList = _.filter(images, {
          status: 'PENDING',
        });
        if (_.size(incompleteList) > 0) {
          await checkTTIInferenceCompletion(userId, incompleteList);
          imageChunk = await fetchTTIInferenceChunk(chunk, scroll ? chunkPage : 0, query, signal);
          images = imageChunk.rows;
        }
        const cleanedData = images.map(({ id, label, status, createdAt, outputFormat, quantity }) => {
          return {
            id,
            label,
            createdAt: moment(createdAt).format('MM/DD/YY hh:mm A'),
            status,
            outputFormat,
            quantity,
            tag: '',
            thumbnail: null,
          };
        });

        // tti Inference - thumbnail image setting
        const withThumbnails = await Promise.all(
          cleanedData.map(async item => {
            try {
              const fileName = `text-to-image_00001_.png`;
              const downloadName = `${item.label}_1.png`;
              const url = await getTTIInferenceSignedUrl(item.id, fileName, downloadName);
              return { ...item, thumbnail: url };
            } catch (e) {
              console.warn(`Preview load failed for ${item.id}`, e);
              return item;
            }
          })
        );

        if (scroll) {
          setData(prevData => [...prevData, ...withThumbnails]);
          setChunkPage(prevChunkPage => prevChunkPage + 1);
        } else {
          setData(withThumbnails);
          setChunkPage(1);
        }
        setLength(imageChunk.count);
      } else if (alignment === 'ttm') {
        let songChunk;
        songChunk = await fetchTTMInferenceChunk(chunk, scroll ? chunkPage : 0, query, signal);
        let songs = songChunk.rows;
        const incompleteList = _.filter(songs, {
          status: 'PENDING',
        });
        if (_.size(incompleteList) > 0) {
          await checkTTMInferenceCompletion(userId, incompleteList);
          songChunk = await fetchTTMInferenceChunk(chunk, scroll ? chunkPage : 0, query, signal);
          songs = songChunk.rows;
        }
        const cleanedData = songs.map(
          ({
            id,
            label,
            tags,
            prompt,
            lyricPrompt,
            customLyrics,
            generatedLyrics,
            instrumental,
            outputFormat,
            voiceModel,
            aiCoverId,
            status,
            createdAt,
          }) => {
            return {
              id,
              label,
              tags,
              prompt,
              lyricPrompt,
              customLyrics,
              generatedLyrics,
              instrumental,
              outputFormat,
              voiceModel,
              aiCoverId,
              status,
              createdAt: moment(createdAt).format('MM/DD/YY hh:mm A'),
            };
          }
        );

        if (scroll) {
          setData(prevData => [...prevData, ...cleanedData]);
          setChunkPage(prevChunkPage => prevChunkPage + 1);
        } else {
          setData(cleanedData);
          setChunkPage(1);
        }
        setLength(songChunk.count);
      } else {
        let videoRendersChunk;
        videoRendersChunk = await fetchVideoRendersChunk(chunk, scroll ? chunkPage : 0, query, signal);
        let videoRenders = videoRendersChunk.rows;

        const incompleteList = _.filter(videoRenders, { status: 'PENDING' });
        if (_.size(incompleteList) > 0) {
          await checkVideoRenderCompletion(userId, incompleteList);
          videoRendersChunk = await fetchVideoRendersChunk(chunk, scroll ? chunkPage : 0, query, signal);
          videoRenders = videoRendersChunk.rows;
        }
        const cleanedData = videoRenders.map(({ id, label, status, createdAt, watermark, deletedAt }) => {
          return {
            id,
            label,
            createdAt: moment(createdAt).format('MM/DD/YY hh:mm A'),
            status,
            deletedAt,
            watermark,
          };
        });

        if (scroll) {
          setData(prevData => [...prevData, ...cleanedData]);
          setChunkPage(prevChunkPage => prevChunkPage + 1);
        } else {
          setData(cleanedData);
          setChunkPage(1);
        }
        setLength(videoRendersChunk.count);
      }
    } catch (err) {
      if (err.name != 'CanceledError') {
        showMessageV2(dispatch, t('historyTab.modal.error'), { reloadOnClose: true });
      }
    }
  };

  const handleDelete = async (id, type, alignment) => {
    setConfirmationModalProps({
      open: true,
      message: type !== 'FAILED' && t('historyTab.confirmDelete'),
      onConfirmation: async response => {
        if (response === true) {
          setConfirmationModalProps({ open: true, loading: true });
          if (alignment === 'music') {
            await updateInferenceById(id, { deletedAt: moment() });
            if (_.size(data) === 1) {
              setVoiceId([]);
              setRenderFilter(!renderFilter);
            }
          } else if (alignment === 'speech') {
            await updateTTSInferenceById(id, { deletedAt: moment() });
          } else if (alignment === 'vocalExtract') {
            await updateVocalExtractById(id, { deletedAt: moment() });
          } else if (alignment === 'faceSwap') {
            await updateFaceSwapById(id, { deletedAt: moment() });
          } else if (alignment === 'image') {
            await updateTTIInferenceById(id, { deletedAt: moment() });
          } else if (alignment === 'ttm') {
            await updateTTMInferenceById(id, { deletedAt: moment() });
          } else {
            await updateVideoRenderById(id, { deletedAt: moment() });
          }

          let copiedData = [...data];
          copiedData = copiedData.filter(obj => obj.id !== id);
          setData(copiedData);
        }
        setConfirmationModalProps({ open: false });
      },
      onClose: () => setConfirmationModalProps({ open: false }),
    });
  };

  const handleEdit = async (id, label, alignment) => {
    setEditModalProps({
      open: true,
      label,
      alignment,
      onEdit: async changedLabel => {
        setEditModalProps({ open: true, loading: true });
        if (alignment === 'music') {
          await updateInferenceById(id, { label: changedLabel });
        } else if (alignment === 'speech') {
          await updateTTSInferenceById(id, { label: changedLabel });
        } else if (alignment === 'vocalExtract') {
          await updateVocalExtractById(id, { label: changedLabel });
        } else if (alignment === 'faceSwap') {
          await updateFaceSwapById(id, { label: changedLabel });
        } else if (alignment === 'ttm') {
          await updateTTMInferenceById(id, { label: changedLabel });
        } else if (alignment === 'image') {
          await updateTTIInferenceById(id, { label: changedLabel });
        } else {
          await updateVideoRenderById(id, { label: changedLabel });
        }
        let copiedData = data.map(obj => {
          if (obj.id === id) {
            return { ...obj, label: changedLabel };
          }
          return obj;
        });
        setData(copiedData);
        setEditModalProps({ open: false });
      },
      onClose: () => {
        setEditModalProps({ open: false });
      },
    });
  };

  const handleDownload = async (id, needMdx, label, alignment, outputFormat, tag, quantity) => {
    setDownloadModalProps({
      open: true,
      id,
      needMdx,
      label,
      alignment,
      outputFormat,
      tag,
      quantity,
      onClose: () => {
        setDownloadModalProps({ open: false });
      },
    });
  };

  const handleTTSDownload = async (id, label) => {
    setDownloadingId(id);
    const tempLink = document.createElement('a');
    const inferenceBlob = await getTTSInferenceBlob(id);
    const url = URL.createObjectURL(inferenceBlob.slice(0, inferenceBlob.size, `audio/mpeg`));
    tempLink.href = url;
    tempLink.download = label.replaceAll(' ', '_') + '.mp3';
    if (tempLink.href) {
      tempLink.click();
      URL.revokeObjectURL(tempLink.href);
    }
    setDownloadingId(null);
  };

  const handleVideoRenderDownload = async (id, label) => {
    const fileName = `${id}-complete.mp4`;
    const downloadName = `${label}.mp4`;
    try {
      const url = await getVideoRenderSignedUrl(id, fileName, downloadName);
      const tempLink = document.createElement('a');
      tempLink.href = url;
      if (tempLink.href) {
        document.body.appendChild(tempLink);
        tempLink.click();
        document.body.removeChild(tempLink);
      }
    } catch (e) {
      console.log('error downloading video render: ', e);
    }
  };

  const handleFaceSwapDownload = async (id, label) => {
    try {
      const { url } = await fetchRenderedVideoUrl(id);
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const tempLink = document.createElement('a');
      tempLink.href = blobUrl;

      let fileName = label ? label : 'download';
      if (!fileName.toLowerCase().endsWith('.mp4')) {
        fileName += '.mp4';
      }

      tempLink.download = fileName;

      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);

      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.log('Download error:', e);
      // showMessageV2(dispatch, t('faceSwapTab.error.downloadFailed'));
    }
  };

  const handleTTMDownload = async (id, label, outputFormat, voiceLabel, aiCoverId, alignment) => {
    setDownloadModalProps({
      open: true,
      id,
      label,
      outputFormat,
      voiceLabel,
      aiCoverId,
      alignment,
      onClose: () => {
        setDownloadModalProps({ open: false });
      },
    });
  };

  const handleTTMInfo = async (label, tags, prompt, lyricPrompt, customLyrics, generatedLyrics, status) => {
    setTTMInfoModalProps({
      open: true,
      label,
      tags,
      prompt,
      lyricPrompt,
      customLyrics,
      generatedLyrics,
      status,
      onTTMInfoModalClose: () => {
        setTTMInfoModalProps({ open: false });
      },
    });
  };

  const classes = useStyles();
  return (
    <>
      <DownloadModal {...downloadModalProps} />
      <ConfirmationModal {...confirmationModalProps} />
      <EditLabelModal {...editModalProps} />
      <TTMInfoModal {...ttmInfoModalProps} />
      <div className={classes.container}>
        <div className={classes.pageTitle}>{t('historyTab.creations.title')}</div>
        <div className={classes.selectContainer}>
          <Select
            value={alignment}
            onChange={e => {
              setAlignment(e.target.value);
              setData([]);
              setQuery('');
              setChunkPage(0);
              setVoiceId([]);
            }}
            size='small'
            autoWidth
            className={classes.select}
            MenuProps={{
              disableScrollLock: true,
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              slotProps: {
                paper: { className: classes.selectPaper },
              },
            }}
          >
            {historyTabs.map(({ title, tab }) => (
              <MenuItem value={tab} key={tab} dense>
                {title}
              </MenuItem>
            ))}
          </Select>
          <input
            className={classes.searchbar}
            placeholder={t('historyTab.searchbar')}
            value={query}
            onChange={query => setQuery(query.target.value)}
          />
        </div>
        {!loading && (

          <>
            {alignment === 'music' && userId && (
              <Autocomplete
                multiple
                limitTags={2}
                size="small"
                slotProps={{
                  paper: { className: classes.autocompletePaper },
                  listbox: { className: classes.autocompleteList },
                }}
                className={classes.autocomplete}
                options={voice}
                onChange={(event, newValues) => {
                  setVoiceId(newValues.map(voice => voice.trainingId));
                }}
                key={renderFilter}
                noOptionsText={t('historyTab.noOptions')}
                onOpen={() => initVoice()}
                renderInput={params =>
                  voiceId.length > 0 ? (
                    <TextField {...params} />
                  ) : (
                    <TextField {...params} placeholder={t('historyTab.voice')} />
                  )
                }
              />
            )}
            {(alignment === 'music' || alignment === 'vocalExtract') && (
              <InfiniteScroll
                className={classes.infiniteScroll}
                dataLength={data.length}
                next={() => initData(true)}
                hasMore={data.length < length}
                loader={<div className={classes.pagination}>{t('historyTab.pagination.loader')}</div>}
              >
                {data
                  .sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)))
                  .map(
                    ({
                      pitch,
                      mixerSettings,
                      id,
                      label,
                      createdAt,
                      status,
                      needMdx,
                      tag,
                      outputFormat,
                      sourceLabel,
                      trainingLabel,
                      pipelineStatus,
                      quantity,
                    }) => {
                      return (
                        <div
                          key={id}
                          className={`${classes.card} ${(alignment === 'music' || alignment === 'vocalExtract') && status === 'COMPLETE' && classes.clickableCard}`}
                          onClick={() => {
                            if ((alignment === 'music' || alignment === 'vocalExtract') && status === 'COMPLETE') {
                              dispatch(appActions.selectEditorSource({ id, label, outputFormat, alignment }));
                              navigate('/video-editor');
                            }
                          }}
                        >
                          <div className={classes.cardLabel}>
                            {label} {tag}
                            <EditIcon
                              className={classes.cardButton}
                              onClick={event => {
                                event.stopPropagation();
                                handleEdit(id, label, alignment);
                              }}
                            />
                          </div>
                          <div className={classes.cardActions}>
                            <div className={classes.cardTimestampContainer}>
                              <div className={classes.cardTimestamp}>{createdAt}</div>
                              {status === 'FAILED' && <div className={classes.cardStatus}>{t('historyTab.failed')}</div>}
                              {status === 'PENDING' && (
                                <div className={classes.cardStatus}>
                                  {pipelineStatus
                                    ? t(`pipeline.inferenceStatusMessage.${pipelineStatus}`)
                                    : t('historyTab.inProgress')}
                                </div>
                              )}
                            </div>
                            <div className={classes.cardButtonGrid}>
                              {(alignment == 'music' || alignment == 'vocalExtract' || alignment == 'image') &&
                                status === 'COMPLETE' && (
                                  <div
                                    className={classes.cardButton}
                                    onClick={event => {
                                      event.stopPropagation();
                                      handleDownload(id, needMdx, label, alignment, outputFormat, tag, quantity);
                                    }}
                                  >
                                    <DownloadIcon />
                                  </div>
                                )}
                              {alignment == 'music' && (
                                <div
                                  className={classes.cardButton}
                                  onClick={event => {
                                    event.stopPropagation();
                                  }}
                                >
                                  <HtmlTooltip
                                    arrow
                                    placement="top"
                                    title={
                                      <div className={'globalTooltipContainer'}>
                                        <div className={'globalTooltipTextContainer'}>
                                          <div className={'globalTooltipTextBold'}>{t('historyTab.infoIcon.source')}</div>
                                          <div className={'globalTooltipText'}>: {sourceLabel}</div>
                                        </div>
                                        <div className={'globalTooltipTextContainer'}>
                                          <div className={'globalTooltipTextBold'}>{t('historyTab.infoIcon.voice')}</div>
                                          <div className={'globalTooltipText'}>: {trainingLabel}</div>
                                        </div>
                                        <div className={'globalTooltipTextContainer'}>
                                          <div className={'globalTooltipTextBold'}>{t('historyTab.infoIcon.pitch')}</div>
                                          <div className={'globalTooltipText'}>: {pitch}</div>
                                        </div>
                                        {mixerSettings?.mixerUsed ? (
                                          <div>
                                            <div className={'globalTooltipTextContainer'}>
                                              <div className={'globalTooltipTextBold'}>
                                                {t('historyTab.infoIcon.mixing.title')}:
                                              </div>
                                            </div>
                                            <div className={'globalTooltipTextContainer'}>
                                              <div className="globalTooltipText globalTooltipTextBullet">&#x2022;</div>
                                              <div className={'globalTooltipTextSmall'}>
                                                {t('historyTab.infoIcon.mixing.vocalLevel')}: {mixerSettings.vocalLevel}dB
                                              </div>
                                            </div>
                                            <div className={'globalTooltipTextContainer'}>
                                              <div className="globalTooltipText globalTooltipTextBullet">&#x2022;</div>
                                              <div className={'globalTooltipTextSmall'}>
                                                {t('historyTab.infoIcon.mixing.reverbLevel')}: {mixerSettings.reverbLevel}
                                              </div>
                                            </div>
                                            <div className={'globalTooltipTextContainer'}>
                                              <div className="globalTooltipText globalTooltipTextBullet">&#x2022;</div>
                                              <div className={'globalTooltipTextSmall'}>
                                                {t('historyTab.infoIcon.mixing.reverbLength')}: {mixerSettings.reverbLength}s
                                              </div>
                                            </div>
                                          </div>
                                        ) : null}
                                        <div className={'globalTooltipTextContainer'}>
                                          <div className={'globalTooltipTextBold'}>{t('historyTab.trainingInfo.status')}</div>
                                          <div className={'globalTooltipText'}>
                                            :{' '}
                                            {status === 'COMPLETE'
                                              ? t('pipeline.trainingStatusMessage.complete')
                                              : pipelineStatus
                                                ? t(`pipeline.inferenceStatusMessage.${pipelineStatus}`)
                                                : status === 'FAILED'
                                                  ? t('pipeline.inferenceStatusMessage.generalMessage')
                                                  : t('pipeline.inferenceStatusMessage.starting')}
                                          </div>
                                        </div>
                                      </div>
                                    }
                                  >
                                    <img src={infoIcon} alt="tooltip-img" />
                                  </HtmlTooltip>
                                </div>
                              )}
                              {(status === 'COMPLETE' || status === 'FAILED') && (
                                <div
                                  className={classes.cardButton}
                                  onClick={event => {
                                    event.stopPropagation();
                                    handleDelete(id, status, alignment);
                                  }}
                                >
                                  <DeleteIcon />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }
                  )}
              </InfiniteScroll>
            )}
            {alignment === 'speech' && (
              <InfiniteScroll
                className={classes.infiniteScroll}
                dataLength={data.length}
                next={() => initData(true)}
                hasMore={data.length < length}
                loader={<div className={classes.pagination}>{t('historyTab.pagination.loader')}</div>}
              >
                {data
                  .sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)))
                  .map(
                    ({ id, label, createdAt, status, needMdx, tag, outputFormat, trainingLabel, variability, similarity }) => {
                      return (
                        <div
                          key={id}
                          className={`${classes.card} ${status === 'COMPLETE' ? classes.clickableCard : ''}`}
                          onClick={() => {
                            if (status === 'COMPLETE') {
                              dispatch(appActions.selectEditorSource({ id, label, outputFormat, alignment }));
                              navigate('/video-editor');
                            }
                          }}
                        >
                          <div className={classes.cardLabel}>
                            {label} {tag}
                            <EditIcon
                              onClick={event => {
                                event.stopPropagation();
                                handleEdit(id, label, alignment);
                              }}
                              className={classes.cardButton}
                            />
                          </div>
                          <div className={classes.cardActions}>
                            <div className={classes.cardTimestampContainer}>
                              <div className={classes.cardTimestamp}>{createdAt}</div>
                              {status === 'FAILED' && <div className={classes.cardStatus}>{t('historyTab.failed')}</div>}
                              {status === 'PENDING' && <div className={classes.cardStatus}>{t('historyTab.inProgress')}</div>}
                              {status === 'COMPLETE' && downloadingId === id && (
                                <div className={classes.cardButton}>
                                  <CircularProgress size="24px" color='inherit' className={classes.circularProgress} />
                                </div>
                              )}
                            </div>
                            <div className={classes.cardButtonGrid}>
                              {downloadingId !== id && status === 'COMPLETE' && (
                                <div className={classes.cardButton}>
                                  <DownloadIcon
                                    onClick={event => {
                                      event.stopPropagation();
                                      handleTTSDownload(id, label);
                                    }}
                                  />
                                </div>
                              )}
                              {status === 'COMPLETE' && (
                                <div className={classes.cardButton}>
                                  <HtmlTooltip
                                    arrow
                                    placement="top"
                                    title={
                                      <div className={'globalTooltipContainer'}>
                                        <div className={'globalTooltipTextContainer'}>
                                          <div className={'globalTooltipTextBold'}>{t('historyTab.infoIcon.tts.voice')}</div>
                                          <div className={'globalTooltipText'}>: {trainingLabel}</div>
                                        </div>
                                        <div className={'globalTooltipTextContainer'}>
                                          <div className={'globalTooltipTextBold'}>
                                            {t('historyTab.infoIcon.tts.variability')}
                                          </div>
                                          <div className={'globalTooltipText'}>: {variability}%</div>
                                        </div>
                                        <div className={'globalTooltipTextContainer'}>
                                          <div className={'globalTooltipTextBold'}>{t('historyTab.infoIcon.tts.similarity')}</div>
                                          <div className={'globalTooltipText'}>: {similarity}%</div>
                                        </div>
                                      </div>
                                    }
                                  >
                                    <img src={infoIcon} alt="tooltip-img" />
                                  </HtmlTooltip>
                                </div>
                              )}
            
                              {(status === 'COMPLETE' || status === 'FAILED') && (
                                <div className={classes.cardButton}>
                                  <DeleteIcon
                                    onClick={event => {
                                      event.stopPropagation();
                                      handleDelete(id, status, alignment);
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }
                  )}
              </InfiniteScroll>
            )}
            {alignment === 'videoEditor' && (
              <InfiniteScroll
                className={classes.infiniteScroll}
                dataLength={data.length}
                next={() => initData(true)}
                hasMore={data.length < length}
                loader={<div className={classes.pagination}>{t('historyTab.pagination.loader')}</div>}
              >
                {data
                  .sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)))
                  .map(({ id, label, createdAt, status }) => {
                    return (
                      <div className={classes.card} key={id}>
                        <div className={classes.cardLabel}>
                          {label}
                          <EditIcon onClick={() => handleEdit(id, label, alignment)} className={classes.cardButton} />
                        </div>
                        <div className={classes.cardActions}>
                          <div className={classes.cardTimestampContainer}>
                            <div className={classes.cardTimestamp}>{createdAt}</div>
                            {status === 'FAILED' && <div className={classes.cardStatus}>{t('historyTab.failed')}</div>}
                            {status === 'PENDING' && <div className={classes.cardStatus}>{t('historyTab.inProgress')}</div>}
                          </div>
                          <div className={classes.cardButtonGrid}>
                            {status === 'COMPLETE' && (
                              <div className={classes.cardButton}>
                                <DownloadIcon
                                  onClick={() => {
                                    handleVideoRenderDownload(id, label);
                                  }}
                                />
                              </div>
                            )}
                            {(status === 'COMPLETE' || status === 'FAILED') && (
                              <div className={classes.cardButton}>
                                <DeleteIcon onClick={() => handleDelete(id, status, alignment)} />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </InfiniteScroll>
            )}
            {alignment === 'faceSwap' && (
              <InfiniteScroll
                className={classes.infiniteScroll}
                dataLength={data.length}
                next={() => initData(true)}
                hasMore={data.length < length}
                loader={<div className={classes.pagination}>{t('historyTab.pagination.loader')}</div>}
              >
                {data
                  .sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)))
                  .map(({ id, label, createdAt, status }) => {
                    return (
                      <div className={classes.card}>
                        <div className={classes.cardLabel}>
                          {label}
                          <EditIcon onClick={() => handleEdit(id, label, alignment)} className={classes.cardButton} />
                        </div>
                        <div className={classes.cardActions}>
                          <div className={classes.cardTimestampContainer}>
                            <div className={classes.cardTimestamp}>{createdAt}</div>
                            {status === 'FAILED' && <div className={classes.cardStatus}>{t('historyTab.failed')}</div>}
                            {status === 'PENDING' && <div className={classes.cardStatus}>{t('historyTab.inProgress')}</div>}
                          </div>
                          <div className={classes.cardButtonGrid}>
                            {status === 'COMPLETE' && (
                              <div className={classes.cardButton}>
                                <DownloadIcon
                                  onClick={() => {
                                    handleFaceSwapDownload(id, label);
                                  }}
                                />
                              </div>
                            )}
                            {(status === 'COMPLETE' || status === 'FAILED') && (
                              <div className={classes.cardButton}>
                                <DeleteIcon onClick={() => handleDelete(id, status, alignment)} />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </InfiniteScroll>
            )}
            {alignment === 'ttm' && (
              <InfiniteScroll
                className={classes.infiniteScroll}
                dataLength={data.length}
                next={() => initData(true)}
                hasMore={data.length < length}
                loader={<div className={classes.pagination}>{t('historyTab.pagination.loader')}</div>}
              >
                {data
                  .sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)))
                  .map(
                    ({
                      id,
                      label,
                      tags,
                      prompt,
                      lyricPrompt,
                      customLyrics,
                      generatedLyrics,
                      instrumental,
                      outputFormat,
                      voiceModel,
                      aiCoverId,
                      status,
                      createdAt,
                    }) => {
                      return (
                        <div className={classes.card} key={id}>
                          <div className={classes.cardLabel}>
                            {label}
                            <EditIcon onClick={() => handleEdit(id, label, alignment)} className={classes.cardButton} />
                          </div>
                          <div className={classes.cardActions}>
                            <div className={classes.cardTimestampContainer}>
                              <div className={classes.cardTimestamp}>{createdAt}</div>
                              {status === 'FAILED' && <div className={classes.cardStatus}>{t('historyTab.failed')}</div>}
                              {status === 'PENDING' && <div className={classes.cardStatus}>{t('historyTab.inProgress')}</div>}
                            </div>
                            <div className={classes.cardButtonGrid}>
                              {status === 'COMPLETE' && (
                                <>
                                  <div className={classes.cardButton}>
                                    <DownloadIcon
                                      onClick={() => {
                                        handleTTMDownload(id, label, outputFormat, voiceModel?.label, aiCoverId, alignment);
                                      }}
                                    />
                                  </div>
                                </>
                              )}
                              <div className={classes.cardButton}>
                                <img
                                  src={infoIcon}
                                  alt="tooltip-img"
                                  onClick={() =>
                                    handleTTMInfo(label, tags, prompt, lyricPrompt, customLyrics, generatedLyrics, status)
                                  }
                                />
                              </div>
                              {(status === 'COMPLETE' || status === 'FAILED') && (
                                <div className={classes.cardButton}>
                                  <DeleteIcon onClick={() => handleDelete(id, status, alignment)} />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }
                  )}
              </InfiniteScroll>
            )}
            {alignment === 'image' && (
              <InfiniteScroll
                className={classes.infiniteScroll}
                dataLength={data.length}
                next={() => initData(true)}
                hasMore={data.length < length}
                loader={<div className={classes.pagination}>{t('historyTab.pagination.loader')}</div>}
              >
                <Grid container xs={12} sm={12} md={12} className={classes.ttiCardGrid}>
                  <div className={classes.selectModelButton} onClick={() => navigate('/generate-tti')}>
                    <AddCircleIcon className={classes.addButtonImg} />
                    <div className={classes.dragAndDropText}>{t('historyTab.tabs.image.makeNewAIImage')}</div>
                  </div>
                  {data
                    .sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)))
                    .map(({ id, label, createdAt, status, thumbnail, needMdx, outputFormat, tag, quantity }) => {
                      return (
                        <Grid className={classes.ttiCard} item container key={id}>
                          <img
                            src={status === 'COMPLETE' ? thumbnail : coverImg}
                            alt={label}
                            className={classes.ttiCardCoverImg}
                          />
                          {(status === 'COMPLETE' || status === 'FAILED') && (
                            <div className={classes.modelDeleteButton} onClick={() => handleDelete(id, status, alignment)}>
                              <DeleteIcon className={classes.modelDeleteIcon} />
                            </div>
                          )}
                          {status === 'COMPLETE' && (
                            <div
                              className={classes.ttiDownloadButton}
                              onClick={e => {
                                e.stopPropagation();
                                handleDownload(id, needMdx, label, alignment, outputFormat, tag, quantity);
                              }}
                            >
                              <DownloadIcon className={classes.modelDeleteIcon} />
                            </div>
                          )}
                          <div className={classes.ttiCardContent}>
                            <div className={classes.ttiCardContentFirstRow}>
                              <span className={classes.ttiCardLabel}>{label}</span>
                              <EditIcon
                                onClick={() => handleEdit(id, label, alignment)}
                                className={classes.ttiCardEditIcon}
                              />
                            </div>
                            <div className={classes.ttiCardTimestamp} style={{ marginBottom: '0.2rem' }}>
                              <ReactTimeAgo date={createdAt} locale="en-US" />
                              {status === 'FAILED' && <div className={classes.ttiCardStatus}>{t('historyTab.failed')}</div>}
                              {status === 'PENDING' && (
                                <div className={classes.ttiCardStatus}>{t('historyTab.inProgress')}</div>
                              )}
                            </div>
                          </div>
                        </Grid>
                      );
                    })}
                </Grid>
              </InfiniteScroll>
            )}
          </>
        )}
      </div>
    </>
  );
}
export default CreationsPage;
